import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@platform/api-client';
import { authApi } from '@/shared/api/auth.api';
import { hasAuthStateCookie } from '@/shared/libs/authState';
import { STALE_TIME } from '@/shared/constants/app/time.constant';
import type {
  AuthUser,
  ConsentState,
  OnboardingState,
} from '@/shared/types/auth.type';
import { authKeys } from './auth.keys';

const ME_RETRY_LIMIT = 2;

/**
 * 이 실패로 "로그인 안 된 상태"라고 단정해도 되는가.
 *
 * 4xx 만 세션 무효다(api-client 의 isSessionRejected 와 같은 기준). 5xx·네트워크·
 * 타임아웃까지 확정 실패로 처리하면 서버가 잠깐 흔들린 것만으로 AuthGuard 가 즉시
 * 로그인 화면으로 보내고, 사용자에겐 "이유 없이 로그아웃됨"으로 보인다.
 */
function isSessionFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.kind === 'SERVER' && error.status >= 400 && error.status < 500;
}

/**
 * 현재 로그인 사용자 조회.
 *
 * 쿠키 기반 인증이므로:
 *  - access_token / refresh_token 은 HttpOnly → JS에서 읽을 수 없음
 *  - 로그인 성공 시 서버가 함께 내려주는 비민감 플래그 쿠키 `auth_state`(httpOnly=false)의
 *    존재 여부로 "로그인되어 있을 가능성"을 판단
 *  - 실제 신원/상태는 /auth/me 호출로 최종 검증 (서버가 access_token을 자동 전송)
 *
 * 주의:
 *  - 401 발생 시 api-client 인터셉터가 refresh를 시도한다.
 *  - refresh가 401/403이면 onUnauthorized가 로그인 페이지로 하드 리다이렉트한다.
 *  - refresh가 5xx·네트워크·타임아웃이면 세션은 그대로 두고 에러만 올라온다.
 */
export function useMe() {
  const enabled = hasAuthStateCookie();

  return useQuery<AuthUser>({
    queryKey: authKeys.me,
    queryFn: () => authApi.me(),
    enabled,
    staleTime: STALE_TIME.MEDIUM,
    // 세션 무효(4xx)는 재시도해봐야 같은 답이다. 일시 장애만 짧게 다시 시도한다.
    retry: (failureCount, error) =>
      !isSessionFailure(error) && failureCount < ME_RETRY_LIMIT,
  });
}

/**
 * 인증 상태 요약.
 *
 * - loading         : auth_state 쿠키가 있고 me 호출 결과가 아직 없는 상태
 * - isAuthenticated : me 가 성공적으로 조회된 경우
 * - sessionExpired  : 서버가 이 자격증명을 거부한 경우(4xx). AuthGuard 가 이때만 로그인으로 보낸다
 *
 * 일시 장애(5xx·네트워크)로 me 가 실패하면 `isAuthenticated` 는 false 지만
 * `sessionExpired` 도 false 다 — 로그인 화면으로 튕기지 않고 재시도/에러 표시로 처리한다.
 */
export function useAuth() {
  const hasState = hasAuthStateCookie();
  const { data: user, isLoading, isError, error } = useMe();

  return {
    user: user ?? null,
    isAuthenticated: hasState && !!user && !isError,
    loading: hasState && isLoading,
    sessionExpired: hasState && isError && isSessionFailure(error),
    /** 서버 장애 등으로 판정 자체가 불가능한 상태. */
    unavailable: hasState && isError && !isSessionFailure(error),
  } as const;
}

/**
 * 회원가입 인증 토큰 검증 (메일 링크 콜백).
 *
 * - 토큰은 1회용(usedAt). StrictMode 의 두 번째 마운트로 인한 중복 호출은 캐시가 막아준다.
 * - 실패 시 자동 재시도까지 들어가면 "두 번 호출됨" 처럼 보이므로 retry off.
 * - 같은 토큰으로 재검증 안 일어나게 무한 stale.
 */
export function useVerifyEmail(token: string | null) {
  return useQuery({
    queryKey: authKeys.verifyEmail(token),
    queryFn: () => authApi.verifyEmail(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/**
 * 이메일 변경 확정 (메일 링크 콜백).
 * useVerifyEmail 과 동일 패턴 — token 1회 호출, 재시도 없음.
 */
export function useConfirmEmailChange(token: string | null) {
  return useQuery({
    queryKey: authKeys.confirmEmailChange(token),
    queryFn: () => authApi.confirmEmailChange(token!),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/**
 * 첫 로그인 온보딩 진행 상태.
 *
 * 세 축(비밀번호 변경 / 이메일 인증 / 휴대폰 인증)이 각각 끝났는지를 서버가 판정해
 * 내려준다. FE 가 status 만 보고 추측하면 비밀번호 초기화처럼 "일부만 되돌아간"
 * 경우를 구분할 수 없다.
 *
 * `staleTime: 0` — 이메일 인증은 메일함에서 링크를 눌러 **다른 탭에서** 끝나므로,
 * 이 화면으로 돌아왔을 때 캐시가 남아 있으면 영원히 다음 단계로 못 넘어간다.
 */
export function useOnboardingState() {
  const enabled = hasAuthStateCookie();

  return useQuery<OnboardingState>({
    queryKey: authKeys.onboarding,
    queryFn: () => authApi.onboardingState(),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** 약관 동의 상태 — 재동의 화면이 어떤 문서를 다시 받아야 하는지 본다. */
export function useConsentState() {
  return useQuery<ConsentState>({
    queryKey: authKeys.consents,
    queryFn: () => authApi.consentState(),
    enabled: hasAuthStateCookie(),
    staleTime: 0,
  });
}
